<script setup lang="ts">
/** One overall-size field (W, H, D) with its provenance chip and limits hint. */
import { computed } from 'vue';
import { fmtMm, parseMm } from '@/domain';
import ProvChip from './ProvChip.vue';
import { snap, ui } from '@/app/state';
import { commitDim, limitsFor } from '@/app/edits';
import { labelOf } from '@/app/navigation';

const props = defineProps<{ k: 'W' | 'H' | 'D' }>();
const id = computed(() => `dim-${props.k}`);
const lim = computed(() => { void snap.value; return limitsFor(props.k); });
const status = computed(() => snap.value.fields[props.k]?.status ?? 'edited');
const err = computed(() => ui.fieldErrors[props.k] ?? null);
const value = computed(() => (id.value in ui.drafts ? ui.drafts[id.value]! : fmtMm(snap.value.design[props.k])));
const backMm = computed(() => fmtMm(snap.value.build.thickness.back * 10));

function onInput(e: Event) { ui.drafts[id.value] = (e.target as HTMLInputElement).value; }
function commit(raw: string) { commitDim(props.k, raw, id.value); }
function onChange(e: Event) { const el = e.target as HTMLInputElement; if (el.isConnected) commit(el.value); }
function onKey(e: KeyboardEvent) {
  const el = e.target as HTMLInputElement;
  if (e.key === 'Enter') { e.preventDefault(); commit(el.value); return; }
  if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
  const r = parseMm(el.value);
  if (!r.ok) return;
  e.preventDefault();
  const step = (e.shiftKey ? 100 : 10) * (e.key === 'ArrowUp' ? 1 : -1);
  const next = fmtMm(r.ticks + step);
  ui.drafts[id.value] = next;
  el.value = next;
  commit(next);
}
</script>

<template>
  <div class="dim" :class="{ 'has-err': !!err }">
    <div class="dim-top"><label class="lbl" :for="id">{{ labelOf(k) }}</label><ProvChip :status="status" compact /></div>
    <div class="input-unit">
      <input
        :id="id"
        class="input mono"
        inputmode="decimal"
        autocomplete="off"
        :value="value"
        :aria-describedby="`hint-${k}${err ? ` err-${k}` : ''}`"
        :aria-invalid="err ? 'true' : undefined"
        @input="onInput"
        @change="onChange"
        @keydown="onKey"
      ><span>mm</span>
    </div>
    <p :id="`hint-${k}`" class="hint">{{ fmtMm(lim.min) }}–{{ fmtMm(lim.max) }}{{ k === 'D' && snap.build.thickness.back ? ` · incl. ${backMm} mm back` : '' }}</p>
  </div>
</template>
